import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  CButton,
  CForm,
  CFormGroup,
  CInput,
  CLabel,
  CModal,
  CModalBody,
  CModalFooter,
  CModalHeader,
} from '@coreui/react';
import * as Yup from 'yup';
import { FormikProvider, useFormik } from 'formik';
import classNames from 'classnames';
import { get } from 'lodash';
import LoadingButton from '../../shared/components/LoadingButton';
import { Organization } from '../../shared/models/organization';
import {
  selectUserOrganizationSavedStatus,
  selectUserOrganizationSavingStatus,
} from '../../store/selectors/store.selectors';
import { EditOrganization } from '../../store/actions/orgHierarchy.action';
import renderError from '../../shared/utils/error-utils';

const validationSchema = Yup.object({
  name: Yup.string().required('Organization Name is required')
});

export interface EditOrgProps{
  show: boolean;
  close: () => void;
  org: Organization;
}

const EditOrgModal = (props: EditOrgProps) => {
  const { show, close, org } = props;
  const dispatch = useDispatch();
  const saving = useSelector(selectUserOrganizationSavingStatus);
  const saved = useSelector(selectUserOrganizationSavedStatus);

  useEffect(() => {
    if (saved) {
      close();
    }
  }, [saved]);

  const onSubmit = (values) => {
    dispatch(new EditOrganization({
      orgId: org._id,
      updates: {
        name: values.name
      }
    }));
  };

  const formik = useFormik({
      initialValues: {
        name: org?.name
      },
      validationSchema,
      onSubmit,
    },
  );

  React.useEffect(() => {
    if (org) {
      formik.resetForm({ values: {
          name: org.name
        }
      });
    }
  }, [org]);

  const isInvalid = (fieldName: string) => get(formik.touched, fieldName) && get(formik.errors, fieldName);


  return (
    show && <CModal className="mt-5 pt-5" id="edit-job-modal" show={ show } onClose={ close } closeOnBackdrop={ false }>
      <CModalHeader closeButton>Edit Organization</CModalHeader>
      <CForm onSubmit={ formik.handleSubmit }>
        <FormikProvider value={formik}>
          <CModalBody>
            <CFormGroup>
              <CLabel htmlFor="org-id">ID</CLabel>
              <CInput
                id="org-id"
                type="text"
                placeholder="ID"
                name="org-oid"
                value={org?._id}
                disabled
              />
            </CFormGroup>
            <CFormGroup>
              <CLabel htmlFor="org-name">Name</CLabel>
              <CInput
                id="org-name"
                type="text"
                placeholder="Name"
                name="name"
                className={classNames({'is-invalid': isInvalid('name')})}
                { ...formik.getFieldProps('name') }
              />
              {isInvalid('name') && <div className="invalid-feedback">{ renderError(formik.errors.name) }</div>}
            </CFormGroup>
          </CModalBody>
          <CModalFooter className="glympse-footer">
            <div className="glympse-btnWrapper">
              <LoadingButton color='info' type='submit' className="pl-5 pr-5 bg-color-orange pl-5 pr-5 border-0 roundedBtn roundBottomShadow" isLoading={ saving }>Save</LoadingButton>
            </div>
            <div className="glympse-btnWrapper">
              <CButton className="glympse-cancelBtn" color="light" shape="rounded-pill" variant="ghost" onClick={ close }>Cancel</CButton>
            </div>
          </CModalFooter>
        </FormikProvider>
      </CForm>
    </CModal>
  );
};

export default EditOrgModal;
